import { obterJson } from './api';
import { config, formatar, url, urlApi } from './config';
import { registrarEvento } from './medicao';
import { buscasRecentes, guardarBuscaRecente, locaisRecentes, pontoDeOrigem, slugDoLocal, urlBusca } from './local';
import { irPara } from './sobreposicao';

// Autocomplete da v36 (acQTpl/acLTpl/acOpen): no computador a lista fica presa embaixo do campo; no celular,
// dentro da busca em tela cheia. Os dados vêm da API (cargos e empresas em anúncios, locais em vagas).
const UF_NOME = { AC: 'Acre', AL: 'Alagoas', AP: 'Amapá', AM: 'Amazonas', BA: 'Bahia', CE: 'Ceará', DF: 'Distrito Federal', ES: 'Espírito Santo', GO: 'Goiás', MA: 'Maranhão', MT: 'Mato Grosso', MS: 'Mato Grosso do Sul', MG: 'Minas Gerais', PA: 'Pará', PB: 'Paraíba', PR: 'Paraná', PE: 'Pernambuco', PI: 'Piauí', RJ: 'Rio de Janeiro', RN: 'Rio Grande do Norte', RS: 'Rio Grande do Sul', RO: 'Rondônia', RR: 'Roraima', SC: 'Santa Catarina', SP: 'São Paulo', SE: 'Sergipe', TO: 'Tocantins' };
const STOP = new Set(['de', 'da', 'do', 'das', 'dos', 'e', 'em', 'para', 'a', 'o']);
const ESPERA_MS = 150;

const deac = s => String(s).normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase();
const tokens = q => deac(q).replace(/[.,/|()-]/g, ' ').split(/\s+/).filter(t => t && !STOP.has(t));
const fmt = n => Number(n).toLocaleString(document.documentElement.lang || 'pt-BR');

// "Taguatinga - Brasília, DF" -> "Taguatinga, Brasília · DF"; "São Paulo, SP" -> "São Paulo · SP"
export function rotuloDoLocal(nome) {
    const m = (nome || '').match(/^(.*) - Brasília, DF$/);
    return m ? `${m[1]}, Brasília · DF` : (nome || '').replace(/, ([A-Z]{2})$/, ' · $1');
}

const _cache = new Map();
async function _buscar(endereco, sinal) {
    if (_cache.has(endereco)) {
        return _cache.get(endereco);
    }
    const resposta = await obterJson(endereco, { sinal });
    if (resposta?.sucesso) {
        if (_cache.size > 50) {
            _cache.delete(_cache.keys().next().value);
        }
        _cache.set(endereco, resposta);
    }
    return resposta;
}

function _icone(nome) {
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('class', 'ico ');
    svg.setAttribute('aria-hidden', 'true');
    const use = document.createElementNS('http://www.w3.org/2000/svg', 'use');
    use.setAttribute('href', `#i-${nome}`);
    svg.append(use);
    return svg;
}

// Negrito no começo de cada palavra que casa com um termo (acHl da v36).
function _destacar(nome, ts) {
    const frag = document.createDocumentFragment();
    (nome || '').split(' ').forEach((w, i) => {
        if (i) {
            frag.append(' ');
        }
        const d = deac(w);
        const t = ts.filter(x => d.startsWith(x)).sort((a, b) => b.length - a.length)[0];
        if (t) {
            const b = document.createElement('b');
            b.textContent = w.slice(0, t.length);
            frag.append(b, w.slice(t.length));
        } else {
            frag.append(w);
        }
    });
    return frag;
}

const _iniciais = s => (s || '').split(/\s+/).filter(w => w.length > 2).slice(0, 2).map(w => w[0]).join('').toUpperCase();

// soPreencher: quando devolve true (a busca vai junto com outras escolhas, como no painel de filtros), a sugestão de cargo,
// de busca recente ou de local só preenche os campos e chama aoPreencher, sem ir para a busca.
export function iniciarAutocomplete({ campos, textos, obterLocal, localDoTexto, aoEscolherLocal, aoUsarLocalizacao, soPreencher, aoPreencher }) {
    let estado = null;
    // Se duas buscas usarem a mesma caixa, cada uma só fecha ou escolhe na caixa que ela mesma desenhou por último (pelo atributo,
    // que vale também entre cópias do módulo).
    const dono = `ac${Math.random().toString(36).slice(2, 10)}`;
    let controlador = null;
    let espera = null;
    const caixaDesktop = document.getElementById('acD');
    const caixaCelular = document.getElementById('sovAc');
    const ehCelular = () => !window.matchMedia('(min-width: 960px)').matches;
    // O local da busca é o que está escrito no "Onde" do mesmo formulário (o ponto do GPS vira a cidade dele).
    const localDaBusca = ctx => localDoTexto(campos.find(c => c.tipo === 'l' && c.ctx === ctx)?.inp?.value);
    const nomeLocal = ctx => localDaBusca(ctx)?.nome || '';

    // ---------- montagem da lista ----------
    const secao = (titulo, itens) => {
        const sec = document.createElement('div');
        sec.className = 'ac__sec';
        sec.setAttribute('role', 'group');
        if (titulo) {
            sec.setAttribute('aria-label', titulo);
            const h = document.createElement('p');
            h.className = 'ac__h';
            h.setAttribute('aria-hidden', 'true');
            h.textContent = titulo;
            sec.append(h);
        }
        const ul = document.createElement('ul');
        ul.setAttribute('role', 'presentation');
        ul.append(...itens);
        sec.append(ul);
        return sec;
    };

    const opcao = (lista, item, icone, principal, sub, n) => {
        const i = lista.length;
        lista.push(item);
        const li = document.createElement('li');
        li.setAttribute('role', 'option');
        li.id = `ac-o-${i}`;
        li.className = 'ac__opt';
        li.dataset.i = String(i);
        li.setAttribute('aria-selected', 'false');
        li.append(icone);
        const t = document.createElement('span');
        t.className = 'ac__t';
        t.append(principal);
        if (sub) {
            const s = document.createElement('small');
            s.textContent = sub;
            t.append(s);
        }
        li.append(t);
        if (n != null) {
            const c = document.createElement('span');
            c.className = 'ac__n';
            c.textContent = n;
            li.append(c);
        }
        return li;
    };

    const nota = texto => {
        const p = document.createElement('p');
        p.className = 'ac__note';
        p.textContent = texto;
        return p;
    };

    const anuncios = n => n === 1 ? textos.anuncio : formatar(textos.anuncios, fmt(n));
    const vagas = n => n === 1 ? textos.vaga : formatar(textos.vagas, fmt(n));

    async function _cargo(q, sinal, ctx) {
        const itens = [];
        const partes = [];
        const local = nomeLocal(ctx);
        const curto = () => local.split(',')[0].trim();
        // A API só sugere a partir de 2 letras: com 1, fica a lista do campo vazio (e não um "nenhum cargo" falso).
        if (q.trim().length < 2) {
            const recentes = buscasRecentes().slice(0, 3);
            if (recentes.length) {
                partes.push(secao(textos.recentes, recentes.map(([rq, rl]) => opcao(itens, { t: 'recente', v: rq, l: rl }, _icone('clock-history'), rq, rl, null))));
            }
            const dados = await _buscar(urlApi(`/vagas/autocomplete?q=&localidade=${encodeURIComponent(local)}`), sinal);
            const mais = (dados?.cargos || []).slice(0, 6);
            if (mais.length) {
                partes.push(secao(local ? formatar(textos.maisAnunciados, curto()) : textos.maisAnunciadosGeral,
                    mais.map(c => opcao(itens, { t: 'cargo', v: c.nome, n: c.anuncios }, _icone('search'), c.nome, '', anuncios(c.anuncios)))));
            }
            return { partes, itens };
        }

        const ts = tokens(q);
        const dados = await _buscar(urlApi(`/vagas/autocomplete?q=${encodeURIComponent(q.trim())}&localidade=${encodeURIComponent(local)}`), sinal);
        const cargos = (dados?.cargos || []).slice(0, 6);
        const empresas = (dados?.empresas || []).slice(0, 3);
        if (cargos.length) {
            partes.push(secao(local ? formatar(textos.cargos, curto()) : textos.cargosGeral,
                cargos.map(c => opcao(itens, { t: 'cargo', v: c.nome, n: c.anuncios }, _icone('search'), _destacar(c.nome, ts), '', anuncios(c.anuncios)))));
        }
        if (empresas.length) {
            partes.push(secao(textos.empresas, empresas.map(e => {
                const logo = document.createElement('span');
                logo.className = 'card__logo';
                logo.setAttribute('aria-hidden', 'true');
                if (e.logo) {
                    const img = document.createElement('img');
                    img.src = e.logo;
                    img.alt = '';
                    logo.append(img);
                } else {
                    logo.textContent = _iniciais(e.nome);
                }
                return opcao(itens, { t: 'emp', v: e.nome, n: e.anuncios }, logo, _destacar(e.nome, ts), '', anuncios(e.anuncios));
            })));
        }
        if (dados?.profissao) {
            partes.push(secao(textos.guia, [opcao(itens, { t: 'prof', href: dados.profissao.url }, _icone('mortarboard'), formatar(textos.guiaItem, dados.profissao.titulo), '', null)]));
        }
        if (!cargos.length && !empresas.length) {
            partes.push(nota(local ? formatar(textos.nenhumCargoEm, curto(), q) : formatar(textos.nenhumCargo, q)));
            partes.push(secao('', [opcao(itens, { t: 'alerta', v: q }, _icone('bell'), formatar(textos.aviseMe, q), '', null)]));
        }
        const bruto = secao('', [opcao(itens, { t: 'raw', v: q }, _icone('search'), formatar(textos.buscarTexto, q), '', null)]);
        bruto.classList.add('ac__sec--raw');
        partes.push(bruto);
        return { partes, itens };
    }

    async function _local(q, sinal) {
        const itens = [];
        // O "perto de você" não existe no hotsite (como a v1): sem a opção da localização.
        const topo = config().hotsite ? [] : [opcao(itens, { t: 'geo' }, _icone('bullseye'), textos.usarLocalizacao, textos.usarLocalizacaoSub, null)];
        // "Totalmente remoto" leva às vagas de trabalho remoto do cargo; no painel de filtros o filtro da modalidade já está na tela.
        if (textos.totalmenteRemoto && !soPreencher?.() && (!q.trim() || /^\s*(rem|hom|tot)|remot|home/i.test(q))) {
            topo.push(opcao(itens, { t: 'remoto' }, _icone('house'), textos.totalmenteRemoto, '', null));
        }
        const partes = topo.length ? [secao('', topo)] : [];
        const item = l => ({ t: l.tipo === 'estado' ? 'uf' : 'cid', v: l.nome, slug: l.slug, label: l.tipo === 'estado' ? formatar(textos.estado, UF_NOME[l.nome] || l.nome, l.nome) : rotuloDoLocal(l.nome), sub: l.tipo === 'estado' ? textos.todoEstado : '', n: l.vagas });

        if (!q.trim()) {
            const semNumero = l => { const it = item(l); return opcao(itens, it, _icone('geo-alt'), it.label, it.sub, null); };
            // Recentes só do navegador, sem perguntar ao servidor: só o que veio de uma sugestão ("Cidade, UF" ou a sigla do
            // estado); texto livre ou com erro de digitação levaria a uma busca vazia.
            const recentes = locaisRecentes()
                .filter(n => UF_NOME[n.toUpperCase()] || /, [A-Z]{2}$/.test(n))
                .slice(0, 2)
                .map(n => (UF_NOME[n.toUpperCase()] ? { nome: n.toUpperCase(), slug: n.toLowerCase(), tipo: 'estado' } : { nome: n, slug: slugDoLocal(n), tipo: 'cidade' }));
            if (recentes.length) {
                partes.push(secao(textos.locaisRecentes, recentes.map(semNumero)));
            }
            // No www, as cidades com vaga mais perto do aparelho ou da conexão (sem ponto, sem a seção); no hotsite, onde a
            // empresa contrata.
            const hotsite = config().hotsite;
            const origem = hotsite ? null : pontoDeOrigem();
            if (hotsite || origem) {
                const dados = await _buscar(urlApi(`/vagas/autocomplete?campo=onde&q=${origem ? `&perto=${origem.texto}` : ''}`), sinal);
                const usados = new Set(recentes.map(l => l.nome.toLowerCase()));
                let lista = ((hotsite ? dados?.maisVagas : dados?.perto) || []).filter(l => !usados.has(l.nome.toLowerCase()));
                if (origem?.daConexao) {
                    // A grade de ~11 km pode pôr a cidade vizinha antes da cidade da conexão: ela vai na frente.
                    const minha = (obterLocal({ soConexao: true })?.nome || '').toLowerCase();
                    lista = [...lista.filter(l => l.nome.toLowerCase() === minha), ...lista.filter(l => l.nome.toLowerCase() !== minha)];
                }
                lista = lista.slice(0, 5);
                if (lista.length) {
                    partes.push(secao(hotsite ? textos.cidadesMaisVagas : textos.cidadesPerto, lista.map(semNumero)));
                }
            }
            return { partes, itens };
        }

        const dados = await _buscar(urlApi(`/vagas/autocomplete?campo=onde&q=${encodeURIComponent(q.trim())}`), sinal);
        const ts = tokens(q);
        const locais = dados?.locais || [];
        if (locais.length) {
            partes.push(secao(textos.locais, locais.map(l => { const it = item(l); return opcao(itens, it, _icone('geo-alt'), _destacar(it.label, ts), it.sub, vagas(it.n)); })));
        } else if (!/remot|home/i.test(q)) {
            partes.push(nota(formatar(textos.nenhumLocal, q)));
        }
        return { partes, itens };
    }

    // ---------- abrir, fechar, navegar ----------
    function fechar() {
        if (!estado) {
            return;
        }
        if (estado.caixa.dataset.acDono === dono) {
            estado.caixa.hidden = true;
            estado.caixa.replaceChildren();
        }
        estado.inp.setAttribute('aria-expanded', 'false');
        estado.inp.removeAttribute('aria-activedescendant');
        if (estado.ctx === 'm') {
            document.querySelector('.sov__body')?.classList.remove('is-ac');
        }
        estado = null;
    }

    function _posicionar(caixa, inp) {
        // Na caixa da busca (.hsearch) o campo é o <label> inteiro (ícone + input): ancorar no input deslocava a lista para a direita do ícone.
        const ancora = inp.closest('.fld, .hsearch label') || inp;
        const r = ancora.getBoundingClientRect();
        const largura = Math.min(Math.max(r.width, 380), window.innerWidth - 16);
        caixa.style.width = largura + 'px';
        caixa.style.left = (Math.max(8, Math.min(r.left, window.innerWidth - largura - 8)) + window.scrollX) + 'px';
        caixa.style.top = (r.bottom + window.scrollY + 6) + 'px';
        // A lista não pode passar do fim da tela: o que sobrasse ficaria cortado e a roda do mouse não teria o que rolar.
        caixa.style.maxHeight = Math.max(160, Math.min(520, window.innerHeight - r.bottom - 18)) + 'px';
    }

    function abrir(campo, editou) {
        const { inp, tipo, ctx } = campo;
        if (ctx === 'd' && ehCelular()) {
            return;
        }
        // No celular, buscas recentes e "mais buscados" ficam na tela até a pessoa digitar no cargo.
        if (ctx === 'm' && !editou && tipo === 'q') {
            fechar();
            return;
        }
        clearTimeout(espera);
        espera = setTimeout(async () => {
            // A resposta pode chegar depois que a pessoa saiu do campo: lista aberta embaixo de campo sem foco não fecha mais.
            if (document.activeElement !== inp) {
                return;
            }
            controlador?.abort();
            controlador = new AbortController();
            const sinal = controlador.signal;
            let montado;
            try {
                montado = tipo === 'q' ? await _cargo(inp.value, sinal, ctx) : await _local(editou ? inp.value : '', sinal);
            } catch (e) {
                if (e.name !== 'AbortError') {
                    fechar();
                }
                return;
            }
            if (document.activeElement !== inp) {
                return;
            }
            const caixa = ctx === 'd' ? caixaDesktop : caixaCelular;
            if (!caixa) {
                return;
            }
            const jaAberta = estado && estado.inp === inp;
            if (estado && estado.caixa !== caixa) {
                fechar();
            }
            caixa.replaceChildren(...montado.partes);
            caixa.hidden = false;
            caixa.dataset.acDono = dono;
            estado = { inp, tipo, ctx, itens: montado.itens, idx: -1, caixa };
            if (ctx === 'm') {
                document.querySelector('.sov__body')?.classList.add('is-ac');
            } else {
                _posicionar(caixa, inp);
            }
            inp.setAttribute('aria-expanded', 'true');
            inp.setAttribute('aria-controls', caixa.id);
            inp.removeAttribute('aria-activedescendant');
            // Como no v1: conta quando a lista abre, não a cada tecla.
            if (!jaAberta && montado.itens.length) {
                registrarEvento('sugestao_exibida', { campo: tipo === 'q' ? 'cargo' : 'local', quantidade: montado.itens.length });
            }
        }, editou ? ESPERA_MS : 0);
    }

    function mover(d) {
        const n = estado.itens.length;
        if (!n) {
            return;
        }
        estado.idx = estado.idx < 0 && d < 0 ? n - 1 : (estado.idx + d + n) % n;
        estado.caixa.querySelectorAll('.ac__opt').forEach(o => o.setAttribute('aria-selected', String(+o.dataset.i === estado.idx)));
        estado.caixa.querySelector(`#ac-o-${estado.idx}`)?.scrollIntoView({ block: 'nearest' });
        estado.inp.setAttribute('aria-activedescendant', `ac-o-${estado.idx}`);
    }

    function escolher(it) {
        const { ctx, inp } = estado;
        // A localização e o "Totalmente Remoto" ficam fixos no topo da lista: a posição conta só as sugestões.
        const sugestoes = estado.itens.filter(x => x.t !== 'geo' && x.t !== 'remoto');
        registrarEvento('sugestao_escolhida', { campo: estado.tipo === 'q' ? 'cargo' : 'local', tipo: it.t, posicao: sugestoes.indexOf(it) });
        fechar();

        if (it.t === 'cargo' || it.t === 'recente' || it.t === 'raw') {
            inp.value = it.v;
            if (it.t !== 'raw' && soPreencher?.()) {
                const campoLocal = campos.find(c => c.tipo === 'l' && c.ctx === ctx)?.inp;
                if (it.t === 'recente' && campoLocal) {
                    campoLocal.value = it.l || '';
                }
                aoPreencher?.();
                return;
            }
            const local = it.t === 'recente' ? (it.l ? { nome: it.l, slug: slugDoLocal(it.l) } : null) : localDaBusca(ctx);
            guardarBuscaRecente(it.v, local?.nome || '');
            irPara(it.t === 'raw'
                ? url(`/vagas?query=${encodeURIComponent(it.v)}&localidade=${encodeURIComponent(local?.nome || '')}`)
                : urlBusca(it.v, local));
            return;
        }
        if (it.t === 'emp') {
            const local = localDaBusca(ctx);
            irPara(url(`/vagas?query=&localidade=${encodeURIComponent(local?.nome || '')}&empresaUnidade=${encodeURIComponent(it.v)}`));
            return;
        }
        if (it.t === 'prof') {
            irPara(it.href);
            return;
        }
        if (it.t === 'alerta') {
            // A busca do cargo digitado abre a janela do alerta já preenchida (?alerta=1; o visitante passa pelo login).
            const local = localDaBusca(ctx);
            irPara(url(`/vagas?query=${encodeURIComponent(it.v)}&localidade=${encodeURIComponent(local?.nome || '')}&alerta=1`));
            return;
        }
        if (it.t === 'geo') {
            aoUsarLocalizacao?.();
            return;
        }
        if (it.t === 'remoto') {
            // O local da landing do atalho da home (vagas-de-trabalho-remoto): sem vaga remota do cargo, a lista vem vazia. O
            // ?modalidade=home-office sumiria no redirecionamento do código de filtro que não tem vaga.
            const cargo = (campos.find(c => c.tipo === 'q' && c.ctx === ctx)?.inp?.value || '').trim();
            const remoto = { nome: 'Trabalho Remoto', slug: 'trabalho-remoto' };
            guardarBuscaRecente(cargo, remoto.nome);
            // Sem cargo, no www, a própria landing (o mesmo canônico e o <title> traduzido); o hotsite busca pela query string.
            irPara(!cargo && !config().hotsite ? url('/vagas-de-trabalho-remoto') : urlBusca(cargo, remoto));
            return;
        }
        inp.value = it.label;
        if (soPreencher?.()) {
            aoPreencher?.();
            return;
        }
        aoEscolherLocal?.({ tipo: it.t === 'uf' ? 'estado' : 'cidade', nome: it.v, slug: it.slug, rotulo: it.label });
    }

    // ---------- eventos ----------
    campos.forEach(campo => {
        const { inp } = campo;
        if (!inp) {
            return;
        }
        inp.setAttribute('role', 'combobox');
        inp.setAttribute('aria-autocomplete', 'list');
        inp.setAttribute('aria-expanded', 'false');
        inp.addEventListener('input', () => abrir(campo, true));
        inp.addEventListener('focus', () => abrir(campo, false));
        inp.addEventListener('blur', () => setTimeout(() => {
            if (estado && estado.inp === inp && document.activeElement !== inp) {
                fechar();
            }
        }, 120));
        inp.addEventListener('keydown', e => {
            if (!estado || estado.inp !== inp) {
                if (e.key === 'ArrowDown') {
                    e.preventDefault();
                    abrir(campo, true);
                }
                return;
            }
            if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                e.preventDefault();
                mover(e.key === 'ArrowDown' ? 1 : -1);
            } else if (e.key === 'Enter' && estado.idx >= 0) {
                e.preventDefault();
                e.stopPropagation();
                escolher(estado.itens[estado.idx]);
            } else if (e.key === 'Escape') {
                e.stopPropagation();
                fechar();
            }
        });
    });

    [caixaDesktop, caixaCelular].filter(Boolean).forEach(caixa => {
        caixa.addEventListener('pointerdown', e => e.preventDefault());
        caixa.addEventListener('click', e => {
            const o = e.target.closest('.ac__opt');
            if (o && estado && estado.caixa === caixa && caixa.dataset.acDono === dono) {
                escolher(estado.itens[+o.dataset.i]);
            }
        });
    });

    window.addEventListener('scroll', () => {
        if (estado && estado.ctx === 'd') {
            fechar();
        }
    }, { passive: true });
    // Só a largura: nas Mensagens o teclado do Android encolhe a página, e o resize de altura fecharia as sugestões abertas no foco.
    let largura = window.innerWidth;
    window.addEventListener('resize', () => {
        if (window.innerWidth !== largura) {
            largura = window.innerWidth;
            fechar();
        }
    });

    return { fechar, abrir: (inp, editou) => { const c = campos.find(x => x.inp === inp); if (c) abrir(c, editou); } };
}
